/**
 * ServiceHoursWidget — Service hours breakdown by participant
 *
 * Shows hours delivered per participant with budget utilization.
 * MN 256B.85: CFSS participant-directed budgets
 */

import React from 'react';
import {
  Card,
  Group,
  Progress,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { IconClock, IconUser } from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_SERVICE_HOURS = gql`
  query GetServiceHours($limit: Int) {
    payrollTimesheets(limit: $limit) {
      id
      employeeId
      totalRegularHours
      totalOvertimeHours
      status
      payPeriodStart
      payPeriodEnd
    }
  }
`;

interface Timesheet {
  id: string;
  employeeId: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  status: string;
  payPeriodStart: string;
  payPeriodEnd: string;
}

interface ParticipantHours {
  participantId: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  budgetUsedPercent: number;
}

interface ServiceHoursWidgetProps {
  title?: string;
  compact?: boolean;
  monthlyBudgetHours?: number;
}

export function ServiceHoursWidget({
  title = 'Service Hours',
  compact = false,
  monthlyBudgetHours = 160,
}: ServiceHoursWidgetProps) {
  const { data, loading } = useQuery<{ payrollTimesheets: Timesheet[] }>(GET_SERVICE_HOURS, {
    variables: { limit: 200 },
  });

  const timesheets = data?.payrollTimesheets ?? [];

  // Aggregate by participant (using employeeId as proxy - backend would provide actual participant)
  const participantMap = new Map<string, ParticipantHours>();

  for (const ts of timesheets) {
    if (ts.status !== 'approved') continue;

    const existing = participantMap.get(ts.employeeId) ?? {
      participantId: ts.employeeId,
      regularHours: 0,
      overtimeHours: 0,
      totalHours: 0,
      budgetUsedPercent: 0,
    };

    existing.regularHours += ts.totalRegularHours;
    existing.overtimeHours += ts.totalOvertimeHours;
    existing.totalHours = existing.regularHours + existing.overtimeHours;
    existing.budgetUsedPercent = Math.round((existing.totalHours / monthlyBudgetHours) * 100);

    participantMap.set(ts.employeeId, existing);
  }

  const participants = Array.from(participantMap.values())
    .sort((a, b) => b.totalHours - a.totalHours)
    .slice(0, compact ? 5 : 10);

  const totalHours = participants.reduce((sum, p) => sum + p.totalHours, 0);

  if (loading) {
    return (
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed">Loading service hours...</Text>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card withBorder padding="md">
        <Group justify="space-between" mb="xs">
          <Text size="sm" c="dimmed">{title}</Text>
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconClock size={14} />
          </ThemeIcon>
        </Group>
        <Text size="xl" fw={700}>{totalHours.toFixed(1)}</Text>
        <Text size="xs" c="dimmed">{participants.length} participants</Text>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconClock size={14} />
          </ThemeIcon>
          <Text fw={600}>{title}</Text>
        </Group>
        <Text size="sm" c="dimmed">{totalHours.toFixed(1)} total hours</Text>
      </Group>

      {participants.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No approved timesheets this period
        </Text>
      ) : (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Participant</Table.Th>
              <Table.Th ta="right">Regular</Table.Th>
              <Table.Th ta="right">OT</Table.Th>
              <Table.Th>Budget Used</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {participants.map(p => (
              <Table.Tr key={p.participantId}>
                <Table.Td>
                  <Group gap="xs">
                    <ThemeIcon size="xs" variant="light" color="gray">
                      <IconUser size={10} />
                    </ThemeIcon>
                    <Text size="sm">{p.participantId}</Text>
                  </Group>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{p.regularHours.toFixed(1)}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c={p.overtimeHours > 0 ? 'orange' : undefined}>
                    {p.overtimeHours.toFixed(1)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Progress
                      value={Math.min(p.budgetUsedPercent, 100)}
                      color={p.budgetUsedPercent > 100 ? 'red' : p.budgetUsedPercent > 80 ? 'yellow' : 'blue'}
                      size="sm"
                    />
                    <Text size="xs" c="dimmed">{p.budgetUsedPercent}%</Text>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}

export default ServiceHoursWidget;
